import React from 'react';
import PropTypes from 'prop-types';

import { SUBSECTIONS_BY_MAIN_SECTION } from '../GlobalSettings';

import { strings, getCurrentLocale } from '../../../../localization';
import EventsSpeeding from './EventsSpeeding';
import EventsAggressiveAccel from './EventsAggressiveAccel';
import EventsTailgatingDetection from './EventsTailgatingDetection';
import EventsCarBumped from './EventsCarBumped';
import EventsHardBraking from './EventsHardBraking';
import EventsHardCornering from './EventsHardCornering';
import EventsIdling from './EventsIdling';
import EventsPossibleImpact from './EventsPossibleImpact';
import EventGeneric from './EventGeneric';
import events from 'APP_ROOT/data/events';


const eventComponentMap = {
    "EventsSpeeding": EventsSpeeding,
    "EventsAggressiveAccel": EventsAggressiveAccel,
    "EventsTailgatingDetection": EventsTailgatingDetection,
    "EventsCarBumped": EventsCarBumped,
    "EventsHardBraking": EventsHardBraking,
    "EventsHardCornering": EventsHardCornering,
    "EventsIdling": EventsIdling,
    "EventsPossibleImpact": EventsPossibleImpact,
    ...events.reduce((acc, event) => {
        acc[event.pascalName] = 'generic';
        return acc;
    }, {})
};

const betaEvents = [
    "EventsTailgatingDetection"
];

const getSortedEvents = () => {
    const eventNames = Object.keys(eventComponentMap);

    const output = eventNames.map(eventName => {
        return {
            name: eventName.replace(/Events/, ''),
            PascalCase: eventName,
            snake_case: eventName.replace(/([A-Z])/g, '_$1')
                                .toLowerCase()
                                .replace(/^_/, '')
                                .replace(/^events_/, ''),
            camelCase: eventName.replace(/^[A-Z]/, c => c.toLowerCase())
        }
    });

    const lang = getCurrentLocale();
        
    output.sort((a, b) => {
        const aTitle = strings[`settings_global_${a.snake_case}_header`] || strings[`settings_global_events_${a.snake_case}_header`] || a.name;
        const bTitle = strings[`settings_global_${b.snake_case}_header`] || strings[`settings_global_events_${b.snake_case}_header`] || b.name;
        
        return aTitle.localeCompare(bTitle, lang);
    });

    return output;
}


export default class Events extends React.PureComponent {

    static propTypes = {

        selectedSubSection: PropTypes.string,

        eventsSpeedingConfigAvailable: PropTypes.bool,
        eventsSpeedingAvailabilityPending: PropTypes.bool.isRequired,
        eventsSpeedingAvailabilityEnabled: PropTypes.bool,
        eventsSpeedingAvailabilityErrorMessage: PropTypes.string,
        applyEventsSpeedingAvailability: PropTypes.func.isRequired,
        timeoutEventsSpeedingAvailability: PropTypes.func.isRequired,

        eventsSpeedingThresholdPending: PropTypes.bool.isRequired,
        eventsSpeedingThreshold: PropTypes.number,
        eventsSpeedingThresholdType: PropTypes.string,
        eventsSpeedingThresholdErrorMessage: PropTypes.string,
        applyEventsSpeedingThreshold: PropTypes.func.isRequired,
        timeoutEventsSpeedingThreshold: PropTypes.func.isRequired,

        unitOfMeasureDistancePending: PropTypes.bool.isRequired,
        unitOfMeasureDistanceIsMetric: PropTypes.bool,
        unitOfMeasureDistanceErrorMessage: PropTypes.string,

        eventsAggressiveAccelConfigAvailable: PropTypes.bool,
        eventsAggressiveAccelAvailabilityPending: PropTypes.bool.isRequired,
        eventsAggressiveAccelAvailabilityEnabled: PropTypes.bool,
        eventsAggressiveAccelAvailabilityErrorMessage: PropTypes.string,
        applyEventsAggressiveAccelAvailability: PropTypes.func.isRequired,
        timeoutEventsAggressiveAccelAvailability: PropTypes.func.isRequired,

        eventsAggressiveAccelThresholdPending: PropTypes.bool.isRequired,
        eventsAggressiveAccelThreshold: PropTypes.number,
        eventsAggressiveAccelThresholdErrorMessage: PropTypes.string,
        applyEventsAggressiveAccelThreshold: PropTypes.func.isRequired,
        timeoutEventsAggressiveAccelThreshold: PropTypes.func.isRequired,

        eventsTailgatingDetectionConfigAvailable: PropTypes.bool,
        eventsTailgatingDetectionAvailabilityPending: PropTypes.bool.isRequired,
        eventsTailgatingDetectionAvailabilityEnabled: PropTypes.bool,
        eventsTailgatingDetectionAvailabilityErrorMessage: PropTypes.string,
        applyEventsTailgatingDetectionAvailability: PropTypes.func.isRequired,
        timeoutEventsTailgatingDetectionAvailability: PropTypes.func.isRequired,

        eventsTailgatingDetectionSpeedThresholdPending: PropTypes.bool.isRequired,
        eventsTailgatingDetectionSpeedThreshold: PropTypes.number,
        eventsTailgatingDetectionSpeedThresholdErrorMessage: PropTypes.string,
        applyEventsTailgatingDetectionSpeedThreshold: PropTypes.func.isRequired,
        timeoutEventsTailgatingDetectionSpeedThreshold: PropTypes.func.isRequired,

        eventsTailgatingDetectionReactionTimeThresholdPending: PropTypes.bool.isRequired,
        eventsTailgatingDetectionReactionTimeThreshold: PropTypes.number,
        eventsTailgatingDetectionReactionTimeThresholdErrorMessage: PropTypes.string,
        applyEventsTailgatingDetectionReactionTimeThreshold: PropTypes.func.isRequired,
        timeoutEventsTailgatingDetectionReactionTimeThreshold: PropTypes.func.isRequired,

        eventsTailgatingDetectionFollowTimeThresholdPending: PropTypes.bool.isRequired,
        eventsTailgatingDetectionFollowTimeThreshold: PropTypes.number,
        eventsTailgatingDetectionFollowTimeThresholdErrorMessage: PropTypes.string,
        applyEventsTailgatingDetectionFollowTimeThreshold: PropTypes.func.isRequired,
        timeoutEventsTailgatingDetectionFollowTimeThreshold: PropTypes.func.isRequired,

        eventsCarBumpedConfigAvailable: PropTypes.bool,
        eventsCarBumpedAvailabilityPending: PropTypes.bool.isRequired,
        eventsCarBumpedAvailabilityEnabled: PropTypes.bool,
        eventsCarBumpedAvailabilityErrorMessage: PropTypes.string,
        applyEventsCarBumpedAvailability: PropTypes.func.isRequired,
        timeoutEventsCarBumpedAvailability: PropTypes.func.isRequired,

        eventsCarBumpedThresholdPending: PropTypes.bool.isRequired,
        eventsCarBumpedThreshold: PropTypes.number,
        eventsCarBumpedThresholdErrorMessage: PropTypes.string,
        applyEventsCarBumpedThreshold: PropTypes.func.isRequired,
        timeoutEventsCarBumpedThreshold: PropTypes.func.isRequired,

        ...events.reduce((acc, e) => {
            acc[`events${e.pascalName}ConfigAvailable`] = PropTypes.bool;
            acc[`events${e.pascalName}AvailabilityPending`] = PropTypes.bool.isRequired;
            acc[`events${e.pascalName}AvailabilityEnabled`] = PropTypes.bool;
            acc[`events${e.pascalName}AvailabilityErrorMessage`] = PropTypes.string;
            acc[`applyEvents${e.pascalName}Availability`] = PropTypes.func.isRequired;
            acc[`timeoutEvents${e.pascalName}Availability`] = PropTypes.func.isRequired;
            acc[`events${e.pascalName}ExpandReference`] = PropTypes.string.isRequired;
            acc[`events${e.pascalName}ExpandReferenceIsExpanded`] = PropTypes.bool.isRequired;
            acc[`showBetaDecoration`] = PropTypes.bool;
            return acc;
        }, {}),

        eventsHardBrakingConfigAvailable: PropTypes.bool,
        eventsHardBrakingAvailabilityPending: PropTypes.bool.isRequired,
        eventsHardBrakingAvailabilityEnabled: PropTypes.bool,
        eventsHardBrakingAvailabilityErrorMessage: PropTypes.string,
        applyEventsHardBrakingAvailability: PropTypes.func.isRequired,
        timeoutEventsHardBrakingAvailability: PropTypes.func.isRequired,

        eventsHardBrakingThresholdPending: PropTypes.bool.isRequired,
        eventsHardBrakingThreshold: PropTypes.number,
        eventsHardBrakingThresholdErrorMessage: PropTypes.string,
        applyEventsHardBrakingThreshold: PropTypes.func.isRequired,
        timeoutEventsHardBrakingThreshold: PropTypes.func.isRequired,

        eventsHardCorneringConfigAvailable: PropTypes.bool,
        eventsHardCorneringAvailabilityPending: PropTypes.bool.isRequired,
        eventsHardCorneringAvailabilityEnabled: PropTypes.bool,
        eventsHardCorneringAvailabilityErrorMessage: PropTypes.string,
        applyEventsHardCorneringAvailability: PropTypes.func.isRequired,
        timeoutEventsHardCorneringAvailability: PropTypes.func.isRequired,

        eventsHardCorneringThresholdPending: PropTypes.bool.isRequired,
        eventsHardCorneringThreshold: PropTypes.number,
        eventsHardCorneringThresholdErrorMessage: PropTypes.string,
        applyEventsHardCorneringThreshold: PropTypes.func.isRequired,
        timeoutEventsHardCorneringThreshold: PropTypes.func.isRequired,

        eventsIdlingConfigAvailable: PropTypes.bool,
        eventsIdlingAvailabilityPending: PropTypes.bool.isRequired,
        eventsIdlingAvailabilityEnabled: PropTypes.bool,
        eventsIdlingAvailabilityErrorMessage: PropTypes.string,
        applyEventsIdlingAvailability: PropTypes.func.isRequired,
        timeoutEventsIdlingAvailability: PropTypes.func.isRequired,

        eventsIdlingThresholdPending: PropTypes.bool.isRequired,
        eventsIdlingThreshold: PropTypes.number,
        eventsIdlingThresholdErrorMessage: PropTypes.string,
        applyEventsIdlingThreshold: PropTypes.func.isRequired,
        timeoutEventsIdlingThreshold: PropTypes.func.isRequired,

        eventsIdlingSpeedThresholdPending: PropTypes.bool.isRequired,
        eventsIdlingSpeedThreshold: PropTypes.number,
        eventsIdlingSpeedThresholdErrorMessage: PropTypes.string,
        applyEventsIdlingSpeedThreshold: PropTypes.func.isRequired,
        timeoutEventsIdlingSpeedThreshold: PropTypes.func.isRequired,
        
        eventsPossibleImpactConfigAvailable: PropTypes.bool,
        eventsPossibleImpactAvailabilityPending: PropTypes.bool.isRequired,
        eventsPossibleImpactAvailabilityEnabled: PropTypes.bool,
        eventsPossibleImpactAvailabilityErrorMessage: PropTypes.string,
        applyEventsPossibleImpactAvailability: PropTypes.func.isRequired,
        timeoutEventsPossibleImpactAvailability: PropTypes.func.isRequired,

        eventsPossibleImpactThresholdPending: PropTypes.bool.isRequired,
        eventsPossibleImpactThreshold: PropTypes.number,
        eventsPossibleImpactThresholdErrorMessage: PropTypes.string,
        applyEventsPossibleImpactThreshold: PropTypes.func.isRequired,
        timeoutEventsPossibleImpactThreshold: PropTypes.func.isRequired,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        eventsExpandReference: PropTypes.string.isRequired,
        eventsExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsSpeedingExpandReference: PropTypes.string.isRequired,
        eventsSpeedingExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsAggressiveAccelExpandReference: PropTypes.string.isRequired,
        eventsAggressiveAccelExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsTailgatingDetectionExpandReference: PropTypes.string.isRequired,
        eventsTailgatingDetectionExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsCarBumpedExpandReference: PropTypes.string.isRequired,
        eventsCarBumpedExpandReferenceIsExpanded: PropTypes.bool.isRequired,


        eventsHardBrakingExpandReference: PropTypes.string.isRequired,
        eventsHardBrakingExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsHardCorneringExpandReference: PropTypes.string.isRequired,
        eventsHardCorneringExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsIdlingExpandReference: PropTypes.string.isRequired,
        eventsIdlingExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        eventsPossibleImpactExpandReference: PropTypes.string.isRequired,
        eventsPossibleImpactExpandReferenceIsExpanded: PropTypes.bool.isRequired,
    }

    constructor(props) {
        super(props);
        this.state = {
            sortedEventNames: getSortedEvents(),
        };
    }

    
    /*componentWillUnmount() {
    }*/

    /*componentDidUpdate(prevProps) {
    }*/

    expandOrCollapseSectionsAndSubsections = (isExpanded) => {
        this.props.expandOrCollapseStateForUIReferences(isExpanded,[
            this.props.eventsExpandReference,
            this.props.eventsSpeedingExpandReference,
            this.props.eventsAggressiveAccelExpandReference,
            this.props.eventsTailgatingDetectionExpandReference,
            this.props.eventsCarBumpedExpandReference,
            ...events.map(event => this.props[`events${event.pascalName}ExpandReference`]),
            this.props.eventsHardBrakingExpandReference,
            this.props.eventsHardCorneringExpandReference,
            this.props.eventsIdlingExpandReference,
            this.props.eventsPossibleImpactExpandReference
        ]);
    }

    customProps = (eventName) => {
        const props = {
            [`${eventName.camelCase}ExpandReference`]: this.props[`${eventName.camelCase}ExpandReference`],
            [`${eventName.camelCase}ExpandReferenceIsExpanded`]: this.props[`${eventName.camelCase}ExpandReferenceIsExpanded`],
        };

        return props;
    }

    render() {
        return (
            <div id="component_document_id_events">
                <h3>
                    <button
                        className={this.props.eventsExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                        onClick={() => {
                            this.expandOrCollapseSectionsAndSubsections(!this.props.eventsExpandReferenceIsExpanded)}
                        }>
                        {strings.settings_global_events_header}
                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                    </button>
                </h3>
                <div>{strings.settings_global_events_description}</div>

                {this.state.sortedEventNames.map(eventName => {

                    if (this.props.selectedSubSection &&
                        SUBSECTIONS_BY_MAIN_SECTION["EVENTS"] &&
                        SUBSECTIONS_BY_MAIN_SECTION["EVENTS"][this.props.selectedSubSection] &&
                        SUBSECTIONS_BY_MAIN_SECTION["EVENTS"][this.props.selectedSubSection].includes((eventName.snake_case)) === false) {
                        return null;
                    };

                    const Component = eventComponentMap[eventName.PascalCase];

                    if(Component === 'generic'){
                        const event = events.find(e => e.pascalName === eventName.PascalCase);
                        return (
                        <EventGeneric
                            key={event.snakeName}
                            eventName={event.name}
                            eventTitle={event.localization.en.title} // You can dynamically set the language as needed
                            eventConfigAvailable={this.props[`events${event.pascalName}ConfigAvailable`]}
                            eventAvailabilityPending={this.props[`events${event.pascalName}AvailabilityPending`]}
                            eventAvailabilityEnabled={this.props[`events${event.pascalName}AvailabilityEnabled`]}
                            eventAvailabilityErrorMessage={this.props[`events${event.pascalName}AvailabilityErrorMessage`]}
                            applyEventAvailability={this.props[`applyEvents${event.pascalName}Availability`]}
                            timeoutEventAvailability={this.props[`timeoutEvents${event.pascalName}Availability`]}
                            expandOrCollapseStateForUIReferences={this.props.expandOrCollapseStateForUIReferences}
                            eventExpandReference={this.props[`events${event.pascalName}ExpandReference`]}
                            eventExpandReferenceIsExpanded={this.props[`events${event.pascalName}ExpandReferenceIsExpanded`]}
                            showBetaDecoration={event.showBetaDecoration}
                            event={event}
                        />);
                    }

                    const customProps = this.customProps(eventName);

                    return (
                        <Component
                            key={eventName.name}
                            showBetaDecoration={betaEvents.includes(eventName.PascalCase)}

                            eventAvailabilityPending={this.props[`${eventName.camelCase}AvailabilityPending`]}
                            eventAvailabilityEnabled={this.props[`${eventName.camelCase}AvailabilityEnabled`]}
                            eventAvailabilityErrorMessage={this.props[`${eventName.camelCase}AvailabilityErrorMessage`]}
                            applyEventAvailability={this.props[`apply${eventName.PascalCase}Availability`]}
                            timeoutEventAvailability={this.props[`timeout${eventName.PascalCase}Availability`]}
                            
                            eventConfigAvailable={this.props[`${eventName.camelCase}ConfigAvailable`]}

                            eventThreshold={this.props[`${eventName.camelCase}Threshold`]}

                            eventSpeedThresholdPending={this.props[`${eventName.camelCase}SpeedThresholdPending`]}
                            eventSpeedThreshold={this.props[`${eventName.camelCase}SpeedThreshold`]}
                            eventSpeedThresholdErrorMessage={this.props[`${eventName.camelCase}SpeedThresholdErrorMessage`]}
                            applyEventSpeedThreshold={this.props[`apply${eventName.PascalCase}SpeedThreshold`]}
                            timeoutEventSpeedThreshold={this.props[`timeout${eventName.PascalCase}SpeedThreshold`]}

                            eventReactionTimeThresholdPending={this.props[`${eventName.camelCase}ReactionTimeThresholdPending`]}
                            eventReactionTimeThreshold={this.props[`${eventName.camelCase}ReactionTimeThreshold`]}
                            eventReactionTimeThresholdErrorMessage={this.props[`${eventName.camelCase}ReactionTimeThresholdErrorMessage`]}
                            applyEventReactionTimeThreshold={this.props[`apply${eventName.PascalCase}ReactionTimeThreshold`]}
                            timeoutEventReactionTimeThreshold={this.props[`timeout${eventName.PascalCase}ReactionTimeThreshold`]}

                            eventFollowTimeThresholdPending={this.props[`${eventName.camelCase}FollowTimeThresholdPending`]}
                            eventFollowTimeThreshold={this.props[`${eventName.camelCase}FollowTimeThreshold`]}
                            eventFollowTimeThresholdErrorMessage={this.props[`${eventName.camelCase}FollowTimeThresholdErrorMessage`]}
                            applyEventFollowTimeThreshold={this.props[`apply${eventName.PascalCase}FollowTimeThreshold`]}
                            timeoutEventFollowTimeThreshold={this.props[`timeout${eventName.PascalCase}FollowTimeThreshold`]}
                            
                            unitOfMeasureDistancePending={this.props.unitOfMeasureDistancePending}
                            unitOfMeasureDistanceIsMetric={this.props.unitOfMeasureDistanceIsMetric}
                            unitOfMeasureDistanceErrorMessage={this.props.unitOfMeasureDistanceErrorMessage}
                            expandOrCollapseStateForUIReferences={this.props.expandOrCollapseStateForUIReferences}

                            // Speeding specific props
                            eventThresholdPending={this.props[`${eventName.camelCase}ThresholdPending`]}
                            eventThresholdType={this.props[`${eventName.camelCase}ThresholdType`]}
                            eventThresholdErrorMessage={this.props[`${eventName.camelCase}ThresholdErrorMessage`]}
                            applyEventThreshold={this.props[`apply${eventName.PascalCase}Threshold`]}
                            timeoutEventThreshold={this.props[`timeout${eventName.PascalCase}Threshold`]}

                            {...customProps}
                        />
                    );
                })}
            </div>
        );
    }
}